import { useEffect, useState, useCallback } from "react";
import { FileSignature } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "../../components/PortalLayout";
import { UploadTile } from "../../components/UploadTile";
import { api, formatApiError } from "../../lib/api";

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";

export default function EmployerDocuments() {
  const [docs, setDocs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/consent").then((r) => setConsents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    try {
      await api.post("/consent/sign", { document_name: "Employer Terms of Engagement", typed_signature: signature, agreed });
      toast.success("Terms signed");
      setSignature(""); setAgreed(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <PortalLayout>
      <div data-testid="employer-documents-page">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Upload centre</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Documents & forms</h1>
        <p className="text-sm text-[#51637a] mt-1">Position descriptions, briefs and signed agreements in one place.</p>

        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          <UploadTile docType="position_description" label="Position descriptions" hint="PDs for your open roles" existingDocs={docs} onChange={load} />
          <UploadTile docType="form" label="Employer briefs & forms" hint="Completed brief documents" existingDocs={docs} onChange={load} />
        </div>

        <div className="ditch-card p-6 mt-6 max-w-2xl" data-testid="employer-consent-card">
          <h2 className="font-heading font-bold text-[#0d2f4d] flex items-center gap-2"><FileSignature size={18} /> Employer Terms of Engagement</h2>
          {consents.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {consents.map((c) => (
                <li key={c.id} className="text-sm text-[#122c47]" data-testid="employer-consent-signed">
                  ✓ {c.document_name} — signed by <strong>{c.typed_signature}</strong> on {new Date(c.signed_at).toLocaleString()}
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="bg-[#fcfbf8] border border-[#e3e0d3] rounded-lg p-4 text-sm text-[#51637a] leading-relaxed max-h-36 overflow-y-auto">
                By signing, you agree that The Ditch Careers will source and shortlist candidates on your behalf, that candidate information shared with you is confidential and used solely for recruitment purposes, and that placement fees apply as agreed with your recruiter.
              </div>
              <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full name as signature"
                data-testid="employer-consent-signature" className={inputCls} />
              <label className="flex items-start gap-3 text-sm text-[#51637a] cursor-pointer">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="employer-consent-checkbox" className="mt-0.5 h-4 w-4 accent-[#C1CC24]" />
                I agree to the Employer Terms of Engagement.
              </label>
              <button onClick={sign} disabled={!signature.trim() || !agreed} data-testid="employer-sign-button"
                className="rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-3 hover:bg-[#1c4d78] transition-colors disabled:opacity-50">
                Sign terms
              </button>
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
